import * as React from 'react';
import { ExpandablePanel, ElementLoader, ComplexValue } from 'ui';
import { AggregationField } from './aggregation-field';
import { ItemTypes, FieldItem } from 'app/area/reports/types';
import { dropPanelForItemType } from 'app/area/reports/components/drop-panel';
import { dragPanelForItemType } from 'app/area/reports/components/drag-panel';
import { AggregationState } from '../types-store';
import { ButtonSelectMultipleDialog } from 'app/area/reports/components/button-select-multiple-dialog';
import { DropArea } from 'app/area/reports/components/drop-area';
import styles from '../../dnd.scss';
import { useCallback } from 'react';

const AggregateDropPanel = dropPanelForItemType(ItemTypes.AGREGATION_FIELD);

const AggregateDragField = dragPanelForItemType(ItemTypes.AGREGATION_FIELD);

export interface AggregationPanelTexts {
  aggregation_area_title: string;
  aggregation_field_info: string;
  aggregation_btn_open_dialog: string;
  aggregation_dialog_title: string;
  aggregation_done: string;
  aggregation_drag_n_drop: string;
  aggregation_remove: string;
  aggregation_options_not_found: string;
  aggregation_page_of: string;
  aggregation_search: string;
  aggregation_how_to_aggregate: string;
  aggregation_unit_of_measure_set: string;
}

export interface AggregationPanelProps {
  orderNumber: string;
  fetching: boolean;

  data: AggregationState;
  selectedAggregationField: ComplexValue<number>;
  selectedAggregationFunction: ComplexValue<number>;
  selectedUomSet: ComplexValue<number>;

  onLoadAggregateFields: () => void;
  onUpdateDefaultAggregateFields: (items: FieldItem[]) => void;
  onSelectAggregateField: (field: FieldItem) => void;
  onSelectAggregateFunction: (aggregateFunction: ComplexValue<number>, uomSet: ComplexValue<number>) => void;
  translation: AggregationPanelTexts;
}

export const AggregationPanel = ({
  data: {
    defaultAggregateFields = [],
    additionalAggregateFields = [],
    aggregateFunctionOptions,
    uomSetOptions
  },
  selectedAggregationField,
  selectedAggregationFunction,
  selectedUomSet,
  onLoadAggregateFields,
  onSelectAggregateField,
  onSelectAggregateFunction,
  onUpdateDefaultAggregateFields,
  translation,
  fetching,
  orderNumber
}: AggregationPanelProps) => {
  
  const onRemove = useCallback(() => {
    onSelectAggregateField(undefined);
  }, [onSelectAggregateField]);

  const handleOnSelect = useCallback((items: FieldItem[]) => {
    const defaultFields = defaultAggregateFields.map(f => f.value);
    const removedRedundandItems = items.filter(i => defaultFields.indexOf(i.value) === -1).map(i => ({added:true, ...i}));
    onUpdateDefaultAggregateFields([
      ...defaultAggregateFields,
      ...removedRedundandItems
    ]);
  }, [defaultAggregateFields, onUpdateDefaultAggregateFields]);

  const selectedFieldValue = selectedAggregationField?.value;
  
  const content = (
    <div className="row">
      <div className="col-6">
        <ul className={styles.dragTagContainer}>
          {defaultAggregateFields.map(item => {
            const isSelected = selectedFieldValue === item.value;
            return <AggregateDragField
              key={item.value}
              className={styles.dragTag}
              field={item}
              isSelected={isSelected}
              onSelect={onSelectAggregateField}
              onRemove={onRemove}
            />;
          })}
          <ButtonSelectMultipleDialog
            className={styles.dragTag}
            options={additionalAggregateFields}
            placeholder={translation.aggregation_search}
            textButtonAdd={`+ ${translation.aggregation_btn_open_dialog}`}
            textTitle={translation.aggregation_dialog_title}
            textDone={translation.aggregation_done}
            textConfirm={translation.aggregation_done}
            textOptionsNotFound={translation.aggregation_options_not_found}
            textPageOf={translation.aggregation_page_of}
            onLoadOptions={onLoadAggregateFields}
            onSelect={handleOnSelect}
          />
        </ul>
      </div>
      <div className="col-6">
        <AggregateDropPanel
          className={styles.dropBox}
          addItem={onSelectAggregateField}
        >
          {aggregateFunctionOptions && selectedFieldValue
            ? <AggregationField
                selectedAggregationField={selectedAggregationField}
                selectedAggregationFunction={selectedAggregationFunction}
                selectedUomSet={selectedUomSet}
                aggregateFunctionOptions={aggregateFunctionOptions}
                uomSetOptions={uomSetOptions}
                onRemove={onRemove}
                onSelect={onSelectAggregateFunction}
                textRemove={translation.aggregation_remove}
                textHowToAggregate={translation.aggregation_how_to_aggregate}
                textUomSet={translation.aggregation_unit_of_measure_set}
            />
            : <DropArea text={translation.aggregation_drag_n_drop} />
          }
        </AggregateDropPanel>
      </div>
    </div>
  );
  
  return (
    <ExpandablePanel title={`${orderNumber}. ${translation.aggregation_area_title}`}>
      {fetching
        ? <ElementLoader />
        : content
      }
    </ExpandablePanel>
  );
};
